import { Link } from 'react-router-dom';
import { ArrowLeft, RefreshCcw, Clock, CheckCircle, XCircle } from 'lucide-react';

export default function RefundPage() {
  return (
    <div className="tt-product min-h-screen bg-background">
      <nav className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-primary">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center gap-3 mb-8">
          <RefreshCcw className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold text-foreground">Refund Policy</h1>
        </div>

        <div className="bg-card rounded-xl shadow-sm border border-border p-8 space-y-6">
          <p className="text-muted-foreground text-sm">Last updated: April 2026</p>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">1. Protected Payment Refunds</h2>
            <p className="text-muted-foreground leading-relaxed">
              Refunds are processed through TrustTrade's protected payment flow. Payments are processed
              and securely held by our third-party payment provider until the transaction conditions are met.
              If a transaction does not complete successfully, funds are returned to the buyer.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">2. When Refunds Apply</h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-success mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Item not delivered</p>
                  <p className="text-sm text-muted-foreground">Full refund if seller fails to deliver within agreed timeframe</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-success mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Item significantly not as described</p>
                  <p className="text-sm text-muted-foreground">Refund eligibility is determined after dispute investigation</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-success mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Transaction cancelled by mutual agreement</p>
                  <p className="text-sm text-muted-foreground">Full refund to buyer, no fees charged</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">3. When Refunds Do Not Apply</h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-destructive mt-0.5" />
                <p className="text-muted-foreground">Buyer confirms receipt and then changes their mind</p>
              </div>
              <div className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-destructive mt-0.5" />
                <p className="text-muted-foreground">Minor differences from description that don't affect functionality</p>
              </div>
              <div className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-destructive mt-0.5" />
                <p className="text-muted-foreground">Funds already released after confirmation. Bank settlement may take up to 2 business days. Released is not the same as Paid.</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">4. Refund Timeline</h2>
            <div className="flex items-start gap-3 p-4 bg-primary/10 rounded-lg">
              <Clock className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium text-foreground">Processing Time</p>
                <p className="text-sm text-muted-foreground">
                  Refunds are processed within 3-5 business days. Bank processing may take
                  an additional 2-3 business days depending on your bank.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">5. Platform Fee Refunds</h2>
            <p className="text-muted-foreground leading-relaxed">
              The 2% platform fee is only charged on successful transactions. If an eligible full refund is
              issued before delivery confirmation, no platform fee is deducted.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">6. How to Request a Refund</h2>
            <p className="text-muted-foreground leading-relaxed">
              To request a refund, open a dispute on your transaction page. Provide evidence
              and description of the issue. Our team will review and process the refund if
              your claim is valid. For immediate assistance, contact{' '}
              <a href="mailto:trusttrade.register@gmail.com" className="text-primary hover:underline">
                trusttrade.register@gmail.com
              </a>
            </p>
          </section>
        </div>
      </main>

      <footer className="border-t border-border py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-muted-foreground text-sm">© 2026 TrustTrade South Africa. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
